'use client'

import { HTMLAttributes, useEffect, useMemo, useRef, useState } from 'react'
import { useKey } from 'react-use'
import { pdf } from '@react-pdf/renderer'
import useEmblaCarousel from 'embla-carousel-react'

import Container from '@/components/base/Container'
import Grid from '@/components/base/Grid'
import Modal from '@/components/base/Modal'
import ProjectDataSheet from '@/components/base/ProjectDataSheet'
import Spinner from '@/components/base/Spinner'
import Typography from '@/components/base/Typography'
import { useStore } from '@/contexts/store'

import { cn } from '@/lib/utils'
import { WPImage, WPPageData } from '@/types'

export type GalleryDownloadProps = HTMLAttributes<HTMLElement> &
  Partial<{
    gallery: WPImage[]
    pageData: WPPageData
  }>

const GalleryDownload = ({ gallery, pageData }: GalleryDownloadProps) => {
  const { strings } = useStore()
  const $scrollbar = useRef<HTMLDivElement>(null)
  const [isLoading, setLoading] = useState(false)
  const [galleryImages, setGalleryImages] = useState(gallery)
  const [selectedIndex, setSelectedIndex] = useState(0)
  const [galleryModal, setGalleryModal] = useState(false)
  const [emblaRef, emblaApi] = useEmblaCarousel({ startIndex: selectedIndex })

  useEffect(() => {
    const imagesForGallery = document.querySelectorAll('[data-gallery="true"]')
    if (imagesForGallery.length === 0) return

    const images = Array.from(imagesForGallery).map((image) => ({
      url: image.getAttribute('src'),
      alt: image.getAttribute('alt'),
      description: image.getAttribute('description')
    }))
    //@ts-ignore
    setGalleryImages([...images, ...gallery])

    imagesForGallery.forEach((image) => {
      const wrapper = image.closest('[data-gallery-wrapper="true"]')
      if (!wrapper) return
      ;(wrapper as HTMLElement).style.cursor = 'pointer'
      wrapper.addEventListener('click', () => {
        setGalleryModal(true)
        const newIndex = Array.from(imagesForGallery).indexOf(image)
        if (newIndex === -1) return
        setSelectedIndex(newIndex)
      })
    })
  }, [])

  const [hasGallery] = useMemo(() => {
    return [gallery && gallery.length > 0]
  }, [gallery])

  const underlineClass = cn([
    'relative inline-block z-10 group',
    'before:absolute before:bottom-0 before:left-0 before:w-full before:h-[.1rem] before:bg-white before:transition-transform before:duration-300 before:ease-out-cubic before:scale-x-0 before:origin-right',
    'group-hover:before:scale-x-100 group-hover:before:origin-left'
  ])

  const onSelect = () => {
    if (!emblaApi) return
    const index = emblaApi.selectedScrollSnap()
    $scrollbar.current?.scrollTo({
      top: index * (68 + 16),
      behavior: 'smooth'
    })
    setSelectedIndex(index)
  }

  emblaApi?.on('reInit', onSelect).on('select', onSelect)

  const goPrev = () => {
    if (!emblaApi || !galleryModal) return
    if (emblaApi?.canScrollPrev()) emblaApi?.scrollPrev()
  }

  const goNext = () => {
    if (!emblaApi || !galleryModal) return
    if (emblaApi?.canScrollNext()) emblaApi?.scrollNext()
  }

  const handleDownload = async () => {
    setLoading(true)
    try {
      const blob = await pdf(<ProjectDataSheet pageData={pageData} />).toBlob()
      const url = URL.createObjectURL(blob)

      // Check if browser is Safari
      const isSafari = /^((?!chrome|android).)*safari/i.test(
        navigator.userAgent
      )

      if (isSafari) {
        // Create download link for Safari
        const link = document.createElement('a')
        link.href = url
        link.download = `${pageData?.slug || 'project'}-datasheet.pdf`
        document.body.appendChild(link)
        link.click()
        document.body.removeChild(link)
      } else {
        // Open in new tab for other browsers
        window.open(url, '_blank')
      }

      // Clean up the URL after use
      setTimeout(() => {
        URL.revokeObjectURL(url)
        setLoading(false)
      }, 100)
    } catch (error) {
      setLoading(false)
      console.error('Errore nella generazione del PDF:', error)
    }
  }

  useKey('ArrowDown', goNext, { event: 'keydown' }, [emblaApi, galleryModal])
  useKey('ArrowRight', goNext, { event: 'keydown' }, [emblaApi, galleryModal])
  useKey('ArrowUp', goPrev, { event: 'keydown' }, [emblaApi, galleryModal])
  useKey('ArrowLeft', goPrev, { event: 'keydown' }, [emblaApi, galleryModal])
  useKey('Escape', () => setGalleryModal(false), { event: 'keydown' }, [])

  return (
    <>
      <Container>
        <Grid>
          {hasGallery ? (
            <div
              className={cn(
                'col-start-0 md:col-start-0 col-span-4 md:col-span-6',
                'lg:col-span-5 lg:col-start-2'
              )}
            >
              <button
                className="group flex h-[12rem] w-full items-center justify-center gap-[1rem] bg-dark-500 text-white md:h-[15rem]"
                onClick={() => setGalleryModal(true)}
              >
                <img
                  src="/icons/ico-gallery.png"
                  alt={strings?.view_gallery_label}
                  className="size-[2.5rem]"
                />
                <Typography variant="label" className={underlineClass}>
                  {strings?.view_gallery_label}
                </Typography>
              </button>
            </div>
          ) : null}
          <div
            className={cn(
              'col-start-0 md:col-start-0 col-span-4 md:col-span-6',
              hasGallery &&
                'mt-[1.6rem] lg:col-span-5 lg:col-start-7 lg:mt-0 xl:col-span-4 xl:col-start-8',
              !hasGallery && 'lg:col-span-6 lg:col-start-4'
            )}
          >
            <button
              type="button"
              onClick={handleDownload}
              className="group flex h-[12rem] w-full items-center justify-center gap-[1rem] bg-dark-600 text-white disabled:opacity-50 md:h-[15rem]"
              disabled={isLoading}
            >
              {isLoading ? (
                <div className="flex size-[2.5rem] items-center justify-center">
                  <Spinner theme="dark" variant="small" />
                </div>
              ) : (
                <img
                  src="/icons/ico-download.png"
                  alt={strings?.download_label}
                  className="size-[2.5rem]"
                />
              )}
              <Typography variant="label" className={underlineClass}>
                {strings?.download_label}
              </Typography>
            </button>
          </div>
        </Grid>
      </Container>
      <Modal open={galleryModal}>
        <div
          className={cn([
            'fixed left-0 top-0 z-modal h-dvh w-full',
            { 'pointer-events-auto': galleryModal }
          ])}
        >
          <Container className="h-full bg-dark-900">
            <div className="flex h-full">
              <div
                ref={$scrollbar}
                className={cn(
                  'hidden h-full w-[15rem] overflow-y-scroll pt-[3rem] md:block'
                )}
                data-lenis-prevent
              >
                {galleryImages?.map((image, index) => (
                  <button
                    key={index}
                    className={cn(
                      'mb-[1.6rem] block h-[6.8rem] w-[12rem] opacity-40 transition-opacity duration-300',
                      selectedIndex === index && 'opacity-100'
                    )}
                    onClick={() => emblaApi?.scrollTo(index)}
                  >
                    <img
                      src={image.url}
                      alt={image.alt}
                      className="h-full w-full object-cover"
                    />
                  </button>
                ))}
              </div>
              <div className="h-full w-full py-[3rem] pr-0 sm:w-[calc(100%-15rem)] sm:p-[3rem]">
                <div className="embla relative">
                  <div className="embla__viewport" ref={emblaRef}>
                    <div className="embla__container">
                      {galleryImages?.map((image, index) => {
                        return (
                          <div key={index} className="embla__slide">
                            <div className="flex h-full flex-col">
                              <div className="h-[calc(100%-10rem)] flex-[0_1_calc(100%-15rem)]">
                                <img
                                  className="m-auto h-full object-contain"
                                  src={image.url}
                                  alt={image.alt}
                                />
                              </div>
                              <div className="flex flex-[1_1_auto] items-end justify-end pb-[.6rem] pr-[15rem] text-white">
                                <Typography
                                  variant="body"
                                  className="leading-[1.8rem]"
                                >
                                  {image.description
                                    ? '<span className="ml-[1rem]">' +
                                      image.description +
                                      '</span>'
                                    : ''}
                                </Typography>
                              </div>
                            </div>
                          </div>
                        )
                      })}
                    </div>
                    <div className="embla__controllers absolute bottom-0 right-0 z-20 flex">
                      <div className="flex items-end justify-end pb-[.6rem] pr-[1rem] text-white">
                        <Typography variant="body" className="leading-[1.8rem]">
                          {`<strong>${selectedIndex < 9 ? '0' + (selectedIndex + 1) : selectedIndex + 1}</strong>`}
                        </Typography>
                      </div>
                      <button
                        className="embla__button flex size-[3rem] rotate-180 items-center justify-center bg-dark-900"
                        onClick={() => emblaApi?.scrollPrev()}
                      >
                        <svg
                          width="8"
                          height="13"
                          viewBox="0 0 8 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M1.1072 1.12044C1.55351 0.674127 2.27713 0.674127 2.72344 1.12044L7.29487 5.69187C7.74118 6.13818 7.74118 6.8618 7.29487 7.30811C6.84856 7.75443 6.12494 7.75443 5.67863 7.30811L1.1072 2.73668C0.660883 2.29037 0.660883 1.56675 1.1072 1.12044Z"
                            fill="white"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M1.1072 11.8795C1.55351 12.3259 2.27713 12.3259 2.72344 11.8795L7.29487 7.30811C7.74118 6.8618 7.74118 6.13818 7.29487 5.69187C6.84856 5.24556 6.12494 5.24556 5.67863 5.69187L1.1072 10.2633C0.660883 10.7096 0.660883 11.4332 1.1072 11.8795Z"
                            fill="white"
                          />
                        </svg>
                      </button>
                      <button
                        className="embla__button flex size-[3rem] items-center justify-center bg-dark-900"
                        onClick={() => emblaApi?.scrollNext()}
                      >
                        <svg
                          width="8"
                          height="13"
                          viewBox="0 0 8 13"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M1.1072 1.12044C1.55351 0.674127 2.27713 0.674127 2.72344 1.12044L7.29487 5.69187C7.74118 6.13818 7.74118 6.8618 7.29487 7.30811C6.84856 7.75443 6.12494 7.75443 5.67863 7.30811L1.1072 2.73668C0.660883 2.29037 0.660883 1.56675 1.1072 1.12044Z"
                            fill="white"
                          />
                          <path
                            fillRule="evenodd"
                            clipRule="evenodd"
                            d="M1.1072 11.8795C1.55351 12.3259 2.27713 12.3259 2.72344 11.8795L7.29487 7.30811C7.74118 6.8618 7.74118 6.13818 7.29487 5.69187C6.84856 5.24556 6.12494 5.24556 5.67863 5.69187L1.1072 10.2633C0.660883 10.7096 0.660883 11.4332 1.1072 11.8795Z"
                            fill="white"
                          />
                        </svg>
                      </button>
                      <button
                        className="embla__button flex size-[3rem] items-center justify-center bg-dark-900"
                        onClick={() => setGalleryModal(false)}
                      >
                        <svg
                          width="12"
                          height="11"
                          viewBox="0 0 12 11"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <rect
                            x="0.443359"
                            y="9.74261"
                            width="14"
                            height="2"
                            rx="1"
                            transform="rotate(-45 0.443359 9.74261)"
                            fill="white"
                          />
                          <rect
                            x="10.3438"
                            y="11.1569"
                            width="14"
                            height="2"
                            rx="1"
                            transform="rotate(-135 10.3438 11.1569)"
                            fill="white"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </Container>
        </div>
      </Modal>
    </>
  )
}

export default GalleryDownload
